<template lang='pug'>
  div(:class='getTargetClass()')
    .coupon-background(v-if='promoCode.attributes.eligible === false')
      .not-eligible
        .row
          .col-icon
            svg.disable.card-icon(:class='getDisableDiscountIcon(promoCode.attributes["type-text"])' role='img' title='discount-type-icons')
          .col-detail
            .promocode-title.limit-two-lines-text.disable(v-text='truncate(promoCode.attributes.title, 48)')
            .label-info.limit-two-lines-text.description.disable(v-text='promoCode.attributes.description')
            .label-info.disable(v-text='promoCode.attributes["validity-text"]')

    .coupon-background(v-else)
      .eligible-coupons
        .row
          .col-icon
            svg.card-icon(:class='getDiscountIcon(promoCode.attributes["type-text"])' role='img' title='discount-type-icons')
          .col-detail
            .promocode-title(:class='getLineLimit()' v-text='truncate(promoCode.attributes.title, 48)')
            CopyPromoCode(v-if='showCodeCopyLabel'
              :promoCodeText='promoCode.attributes.code'
              :isAutoApply='promoCode.attributes["auto-apply"]'
              :cardSequenceNumber='cardSequenceNumber'
              :parentContainer='"promoCodeCard"'
            )
            .label-info.description(:class='getLineLimit()' v-text='promoCode.attributes.description')
            .label-info.limit-one-lines-text(v-text='promoCode.attributes["validity-text"]')
          .col-select(v-if='showCouponRadioButton')
            PromoCodeSelect(:code='promoCode.attributes.code')
          .col-details.col-lg-3(v-if='requestFromListingPage() && !isMobile')
            a.coupon-view-detail-btn(@click='showPromoDetails(promoCode)' data-target='#promo-codes-details-modal' data-toggle='modal')
              center(v-t='"viewDetail"')

    .row.card-btn-row
      div.card-btn(:class='isBrowseProductsButtonOnDfyEnabled ? "col-6" : "col-12"')
        a.coupon-view-detail-btn(v-if='!requestFromListingPage() || isMobile' @click='showPromoDetails(promoCode)' data-target='#promo-codes-details-modal' data-toggle='modal')
          center(v-t='"viewDetail"')

      .col-6.card-btn(v-if='isBrowseProductsButtonOnDfyEnabled')
        BrowseProductsButton(
          :promoCodeAttrs='promoCode.attributes'
        )

    .eligibility-block(v-if='requestFromList() && promoCode.attributes["eligibility-criteria-texts"].length > 0')
      center.eligibility-text(v-text='firstEligibilityCriteria("text")')
</template>

<i18n>
{
  "lng_en": {
    "viewDetail": "VIEW DETAILS"
  },
  "lng_zh": {
    "viewDetail": "查看詳情"
  },
  "lng_th": {
    "viewDetail": "ดูรายละเอียด"
  },
  "lng_id": {
    "viewDetail": "MELIHAT RINCIAN"
  },
  "lng_ko": {
    "viewDetail": "세부 정보보기"
  }
}
</i18n>

<script>
  import CopyPromoCode from '@/components/shared/promo-code/copy-promo-code'
  import BrowseProductsButton from '@/components/offers/browse-products-button'
  import PromoCodeSelect from '@/components/offers/promo-code-select'
  import VisibilityObserver from '@/modules/services/visibility-observer'
  import { pushDataLayerEvent } from '@/modules/services/gtm'
  import { promoCodeMixin } from '@/mixins/promo-code-mixin'

  export default {
    name: 'PromoCodeCard',

    mixins: [
      promoCodeMixin
    ],
    data () {
      return {
        observer: (this.requestFromCarousel() || this.requestFromList()) ? this.visibilityObserver() : null
      }
    },
    computed: {
      isMobile: () => vueSupport.isMobile,
      isBrowseProductsButtonOnDfyEnabled () {
        return this.$kameleoon?.isFeatureFlagActive('enable_browse_products_btn_on_dfy') &&
          this.browseProductsButtonEnabled
      }
    },
    props: {
      promoCode: {
        type: Object,
        default: () => {}
      },
      showCodeCopyLabel: {
        type: Boolean,
        default: false
      },
      showCouponRadioButton: {
        type: Boolean,
        default: true
      },
      targetContainer: {
        type: String,
        default: 'list'
      },
      cardSequenceNumber: {
        type: Number,
        default: -1
      }
    },
    components: {
      CopyPromoCode,
      PromoCodeSelect,
      BrowseProductsButton
    },
    mounted () {
      if (this.observer) {
        this.observer.observe(this.$el)
      }
    },
    destroyed () {
      if (this.observer) {
        this.observer.unobserve(this.$el)
      }
    },
    methods: {
      getDiscountIcon (textValue) {
        switch (textValue) {
          case 'DISCOUNT':
            return 'discount-icon'

          case 'BEAUTY PASS POINTS':
            return 'beauty-pass-icon'

          case 'GIFT WITH PURCHASE':
            return 'rewards-icon'

          default:
            return 'discount-icon'
        }
      },
      getLineLimit () {
        return this.requestFromListingPage() && !this.isMobile ? 'limit-one-lines-text' : 'limit-two-lines-text'
      },
      getDisableDiscountIcon (textValue) {
        switch (textValue) {
          case 'DISCOUNT':
            return 'grey-discount-icon'

          case 'BEAUTY PASS POINTS':
            return 'grey-beauty-pass-icon'

          case 'GIFT WITH PURCHASE':
            return 'grey-rewards-icon'

          default:
            return 'grey-discount-icon'
        }
      },
      getTargetClass () {
        switch (this.targetContainer) {
          case 'carousel':
            return 'carousel-style'

          case 'list':
            return 'list-style'

          case 'listing-page':
            return 'listing-page-style'
        }
      },
      visibilityObserver () {
        const rootMargin = vueSupport.isMobile ? '-115px 0px -45px 0px' : '-75px 0px 0px 0px'
        const opts = {
          intersectionRatio: 1.0,
          rootMargin: rootMargin
        }
        return VisibilityObserver(this.recordImpressionGtmEvent, opts)
      },
      recordImpressionGtmEvent () {
        switch (this.targetContainer) {
          case 'list':
            pushDataLayerEvent(
              'coupon_card_impression', {
                params: {
                  deal_sequence: this.cardSequenceNumber,
                  coupon_code: this.promoCode.attributes.code,
                  eligibility: this.promoCode.attributes.eligible ? 'eligible'
                  : `ineligible - ${this.firstEligibilityCriteria('type')}`
                }
              }
            )
            break
          case 'carousel':
          pushDataLayerEvent(
            'deals_for_you_card_impression', {
              params: {
                deal_sequence: this.cardSequenceNumber,
                coupon_code: this.promoCode.attributes.code || this.impressionOfferId()
              }
            }
          )
          break
        }
      },
      showPromoDetails (promoCode) {
        const promoCodeAttrs = promoCode.attributes
        promoCodeAttrs['card-sequence-number'] = this.cardSequenceNumber
        this.$emit('viewDetailsBtnClicked', promoCode)
        this.recordClickOnPromoCodeDetailsGtm(promoCodeAttrs)
      },
      recordClickOnPromoCodeDetailsGtm (promoCodeAttrs) {
        switch (this.targetContainer) {
          case 'list':
            pushDataLayerEvent(
                'view_promo_details', {
                  params: {
                    event_action: 'View Promo Details',
                    event_category: 'Promo Code Listing',
                    event_label: promoCodeAttrs.code
                  }
                }
              )
            break
          case 'carousel':
            pushDataLayerEvent(
                'deals_for_you_card_view_details', {
                  params: {
                    deal_sequence: this.cardSequenceNumber,
                    coupon_code: promoCodeAttrs.code || this.impressionOfferId()
                  }
                }
              )
            break
        }
      },
      truncate (text, length) {
        if (text == null) {
          return ''
        }

        if (text.length > length) {
          return `${text.substring(0, length)}`
        }
        return text
      },
      requestFromCarousel () {
        return this.targetContainer === 'carousel'
      },
      requestFromList () {
        return this.targetContainer === 'list'
      },
      requestFromListingPage () {
        return (this.targetContainer === 'listing-page')
      },
      firstEligibilityCriteria (key) {
        return this.promoCode.attributes['eligibility-criteria-texts'][0][`eligibility-criteria-${key}`]
      },
      impressionOfferId () {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        if (uuidRegex.test(this.promoCode.id)) {
          return this.promoCode.id
        }
        return this.promoCode.attributes['offer-id']
      }
    }
  }
</script>

<style lang='scss' scoped>
  .card-btn-row{
    padding: 0px 15px;

    @media screen and (max-width: 480px) {
      padding: 0px 10px;
    }
  }
  .card-btn {
    padding: 0px;
  }
  .coupon-view-detail-btn {
    outline: 1px solid;
    outline-offset: -1px;
    padding: 10px 0;
    font-weight: 700;
    margin-right: 15px;
    background-color: $white;
    color: $black;
    width: 100%;
    display: block;
    margin-top: auto;
    cursor: pointer;
  }
  .disable {
    color: $grey9;
  }
  .coupon-background {
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;

    .col-icon {
      padding: 3px 10px;
    }
    .col-detail {
      flex: 1;
      .description.disable {
        width: calc(100% - 30px);
      }
    }
    .col-details {
      margin-top: 55px;

      @include media-breakpoint-down(md) {
        margin-top: 17px;
        padding: 0px;
      }
    }
    .col-select {
      flex: 1;
      max-width: fit-content;
      margin: auto;
      padding: 5px;
    }
    .label-info {
      font-size: 14px;
      padding-top: 8px;
    }
    .card-icon {
      width: 23px;
      height: 22px;
      float: left;
      margin-top: -2px;
    }
    .promocode-title {
      font-weight: 700;
      font-size: 16px;
      text-transform: uppercase;
      min-height: 20px;
    }
    .grey-discount-icon {
      background-image: url('../../assets/images/promo-code/grey-discount.svg');
    }
    .grey-beauty-pass-icon {
      background-image: url('../../assets/images/promo-code/grey-beauty-pass.svg');
    }
    .grey-rewards-icon {
      background-image: url('../../assets/images/promo-code/grey-rewards.svg');
    }
    .discount-icon {
      background-image: url('../../assets/images/promo-code/discount.svg');
    }
    .beauty-pass-icon {
      background-image: url('../../assets/images/promo-code/beauty-pass.svg');
    }
    .rewards-icon {
      background-image: url('../../assets/images/promo-code/rewards.svg');
    }
  }

  .list-style {
    background-size: auto;
    background-image: url('../../assets/images/promo-code/coupon-bg.svg');

    .coupon-background {
      height: 115px;
      border-right: 1px solid $grey;
    }
  }

  .carousel-style {
    background-size: auto;
    background-image: url('../../assets/images/promo-code/coupon-bg-l.svg');

    .coupon-background {
      height: 184px;
      border-right: 1px solid $grey;
    }
  }

  .listing-page-style {
    background-size: 848px 184px;
    background-image: url('../../assets/images/promo-code/coupon-bg-l.png');

    .coupon-background {
      height: 184px;
      border-right: 1px solid $grey;
    }
  }

  .eligibility-text {
    background: $background-blue;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    font-size: 14px;
    letter-spacing: 0.257143px;
    padding: 5px 16px;
  }
  .limit-two-lines-text {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    word-break: break-word;
  }
  .limit-one-lines-text {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    word-break: break-word;
  }
</style>
